import React, { useState, useEffect } from "react";
import {
  FaUser,
  FaCar,
  FaCalendarAlt,
  FaDollarSign,
  FaCheckCircle,
  FaClock,
  FaCog,
  FaEye,
} from "react-icons/fa";
import Sidebar from "../components/SideBar";
import getTransactions from "../Functions/transactionsFunct";
import { ShimmerTransactionCard, ShimmerStatsCard } from "../components/ShimmerComponents";
import useStore from "../store/useStore";

const TransactionCard = ({ transaction }) => {
  const formatDate = (timestamp) => {
    let date;
    
    // Handle Firebase Timestamp objects
    if (timestamp && typeof timestamp.toDate === 'function') {
      date = timestamp.toDate();
    } else if (timestamp && timestamp.seconds) {
      // Handle timestamp with seconds property
      date = new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000);
    } else {
      date = new Date(timestamp);
    }
    
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      confirmed: "bg-green-100 text-green-700",
      pending: "bg-yellow-100 text-yellow-700",
      cancelled: "bg-red-100 text-red-700",
    };
    return `px-2 py-1 rounded-full text-xs font-medium ${
      badges[status] || badges.pending
    }`;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <FaUser className="text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">
              {transaction.scannedUser.firstName}{" "}
              {transaction.scannedUser.lastName}
            </h3>
            <p className="text-sm text-gray-500">
              {transaction.scannedUser.email}
            </p>
          </div>
        </div>
        <div className="text-right">
          <div className="flex items-center space-x-2 mb-1">
            <FaCalendarAlt className="text-gray-400 text-sm" />
            <span className="text-sm text-gray-600">
              {formatDate(transaction.createdAt)}
            </span>
          </div>
          <span className={getStatusBadge(transaction.status)}>
            {transaction.status || "pending"}
          </span>
        </div>
      </div>

      {/* Vehicle Info */}
      <div className="bg-gray-50 rounded-lg p-4 mb-4">
        <div className="flex items-center space-x-2 mb-2">
          <FaCar className="text-gray-600" />
          <span className="font-medium text-gray-900">Vehicle Details</span>
        </div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Plate:</span>
            <span className="ml-2 font-medium">
              {transaction.scannedUser.Plateno}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Make/Model:</span>
            <span className="ml-2 font-medium">
              {transaction.scannedUser.year} {transaction.scannedUser.make} {transaction.scannedUser.model}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Mileage:</span>
            <span className="ml-2 font-medium">
              {transaction.scannedUser.mileage}
            </span>
          </div>
          <div>
            <span className="text-gray-500">VIN:</span>
            <span className="ml-2 font-medium text-gray-900">
              {transaction.scannedUser.vin 
                ? 'X'.repeat(Math.max(0, transaction.scannedUser.vin.length - 6)) + transaction.scannedUser.vin.slice(-6) 
                : "N/A"}
            </span>
          </div>
        </div>
      </div>

      {/* Services */}
      <div className="mb-4">
        <div className="flex items-center space-x-2 mb-3">
          <FaCog className="text-gray-600" />
          <span className="font-medium text-gray-900">Services</span>
        </div>
        <div className="space-y-2">
          {transaction.services.map((service, index) => (
            <div
              key={index}
              className="flex justify-between items-center bg-gray-50 rounded-lg p-3"
            >
              <div>
                <span className="font-medium text-gray-900">
                  {service.serviceName}
                </span>
                <span className="text-sm text-gray-500 ml-2">
                  (Qty: {service.selectedQuantity})
                </span>
              </div>
              <div className="text-right">
                <div className="font-semibold text-gray-900">
                  ${service.price}
                </div>
                <div className="text-sm text-gray-500">
                  Total: ${(service.price * (service.selectedQuantity || 1)).toFixed(2)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Total Amount */}
      <div className="border-t border-gray-200 pt-4">
        <div className="flex justify-between items-center">
          <span className="text-lg font-semibold text-gray-900">
            Total Amount
          </span>
          <span className="text-xl font-bold text-green-600">
            ${transaction.totalAmount}
          </span>
        </div>
      </div>
    </div>
  );
};

const Transactions = () => {
  const cachedTransactions = useStore((state) => state.transactions);
  const transactionsLoaded = useStore((state) => state.transactionsLoaded);
  const setTransactions = useStore((state) => state.setTransactions);

  const [transactions, setLocalTransactions] = useState(cachedTransactions || []);
  const [loading, setLoading] = useState(!transactionsLoaded);

  useEffect(() => {
    const unsubscribe = getTransactions((data) => {
      // Sort transactions by date (newest first)
      const sortedTransactions = data.sort((a, b) => {
        const dateA = a.createdAt && typeof a.createdAt.toDate === 'function' 
          ? a.createdAt.toDate() 
          : a.createdAt && a.createdAt.seconds 
          ? new Date(a.createdAt.seconds * 1000 + (a.createdAt.nanoseconds || 0) / 1000000)
          : new Date(a.createdAt);
        
        const dateB = b.createdAt && typeof b.createdAt.toDate === 'function' 
          ? b.createdAt.toDate() 
          : b.createdAt && b.createdAt.seconds 
          ? new Date(b.createdAt.seconds * 1000 + (b.createdAt.nanoseconds || 0) / 1000000)
          : new Date(b.createdAt);
        
        return dateB - dateA; // Descending order (newest first)
      });
      
      setLocalTransactions(sortedTransactions);
      setTransactions(sortedTransactions); // Cache in store
      setLoading(false);
    });

    // Cleanup subscription on component unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  // Function to group transactions by date
  const groupTransactionsByDate = (transactions) => {
    const groups = {};
    
    transactions.forEach(transaction => {
      let date;
      
      // Handle different timestamp formats
      if (transaction.createdAt && typeof transaction.createdAt.toDate === 'function') {
        date = transaction.createdAt.toDate();
      } else if (transaction.createdAt && transaction.createdAt.seconds) {
        date = new Date(transaction.createdAt.seconds * 1000 + (transaction.createdAt.nanoseconds || 0) / 1000000);
      } else {
        date = new Date(transaction.createdAt);
      }
      
      const dateKey = date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        weekday: "long"
      });
      
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(transaction);
    });
    
    return groups;
  };

  const groupedTransactions = groupTransactionsByDate(transactions);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Sidebar />

      <main className="ml-80 p-8 lg:p-10 min-h-screen">
        <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Transactions
          </h1>
          <p className="text-gray-600">
            View and manage all transaction records
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {loading ? (
            // Show shimmer stats
            Array.from({ length: 3 }).map((_, index) => (
              <ShimmerStatsCard key={index} />
            ))
          ) : (
            <>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <FaDollarSign className="text-blue-600 text-xl" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Revenue</p>
                    <p className="text-2xl font-bold text-gray-900">
                      ${transactions.reduce((sum, transaction) => sum + (transaction.totalAmount || 0), 0)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <FaCheckCircle className="text-green-600 text-xl" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Completed</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {transactions.filter(transaction => transaction.status === 'confirmed').length}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <FaClock className="text-yellow-600 text-xl" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Pending</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {transactions.filter(transaction => transaction.status === 'pending').length}
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="space-y-6">
            {Array.from({ length: 3 }).map((_, index) => (
              <ShimmerTransactionCard key={index} />
            ))}
          </div>
        )}

        {/* Transactions List */}
        {!loading && (
          <div className="space-y-8">
            {Object.entries(groupedTransactions).map(([dateKey, dayTransactions]) => (
              <div key={dateKey} className="space-y-4">
                {/* Date Header */}
                <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg shadow-md">
                  <div className="flex justify-between items-center">
                    <h2 className="text-lg font-semibold">{dateKey}</h2>
                    <span className="text-sm bg-blue-500 px-3 py-1 rounded-full">
                      {dayTransactions.length} transaction{dayTransactions.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
                
                {/* Transactions for this date */}
                <div className="space-y-4">
                  {dayTransactions.map((transaction) => (
                    <TransactionCard key={transaction.id} transaction={transaction} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && transactions.length === 0 && (
          <div className="text-center py-12">
            <FaEye className="text-gray-400 text-6xl mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No transactions found
            </h3>
            <p className="text-gray-500">
              Transactions will appear here once customers make bookings.
            </p>
          </div>
        )}
        </div>
      </main>
    </div>
  );
};

export default Transactions;

